/**
 * ADDRESS LIST EDITOR — Gestione inline della lista indirizzi di un
 * Business (owner unico per Address nel CRM corrente).
 *
 * Ogni address e' reso come un accordion con:
 *   - summary: stella "primario" + label identificativo (name, altrimenti
 *     street+city) + elimina. La stella sta qui e non nei details per
 *     restare leggibile anche ad accordion chiuso, come nelle righe di
 *     EmailListEditor/PhoneListEditor.
 *   - details: EditableField per name, description, street, city, zip,
 *     CountrySelect + RegionSelect editabili (cache geography).
 *
 * Pattern commit per country/region: immediato `onChange` (no draft
 * locale). Cambio country → 1 PATCH `{countryId, regionId: null}` (reset
 * region perche' potrebbe non essere valida nel nuovo country). Cambio
 * region → 1 PATCH `{regionId}`. Cambio completo country+region = 2
 * round-trip — accettato per UX reattiva.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { useAppDispatch } from '../store/hooks';
import { useToast } from './useToast';
import { ConfirmDialog } from './ConfirmDialog';
import { useConfirmDialog } from './useConfirmDialog';
import { EditableField, type EditableValue } from './EditableField';
import { CountrySelect } from './CountrySelect';
import { RegionSelect } from './RegionSelect';
import { addressText, type Address } from '../entities/address/addressTypes';
import { addAddress, updateAddress, removeAddress } from '../entities/address/addressThunks';

interface AddressListEditorProps {
  businessId: number;
  addresses: Address[];
}

type AddressStringField = 'name' | 'description' | 'street' | 'city' | 'zip';

export function AddressListEditor({ businessId, addresses }: AddressListEditorProps) {
  const dispatch = useAppDispatch();
  const toast = useToast();
  const { t } = useTranslation();
  const [isAdding, setIsAdding] = useState(false);
  // FILO-18: il cestino chiede conferma, la cancellazione non si può disfare.
  const confirm = useConfirmDialog();

  const commitAddressField =
    (address: Address, field: AddressStringField) =>
    async (newValue: EditableValue) => {
      const str = String(newValue ?? '').trim();
      try {
        await dispatch(
          updateAddress({
            businessId,
            addressId: address.addressId,
            [field]: str,
          })
        ).unwrap();
        toast({ severity: 'success', message: t('common.saved') });
      } catch (err) {
        toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
        throw err;
      }
    };

  async function handleTogglePrimary(address: Address) {
    if (address.primary) return;
    try {
      await dispatch(
        updateAddress({
          businessId,
          addressId: address.addressId,
          primary: true,
        })
      ).unwrap();
      toast({ severity: 'success', message: t('common.saved') });
    } catch (err) {
      toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
    }
  }

  async function handleCountryChange(address: Address, countryId: number | null) {
    if (countryId === (address.countryId ?? null)) return;
    try {
      // Reset region: la region precedente potrebbe non essere valida nel
      // nuovo country, e l'utente sceglierà la nuova region in un secondo
      // PATCH separato.
      await dispatch(
        updateAddress({
          businessId,
          addressId: address.addressId,
          countryId,
          regionId: null,
        })
      ).unwrap();
      toast({ severity: 'success', message: t('common.saved') });
    } catch (err) {
      toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
    }
  }

  async function handleRegionChange(address: Address, regionId: number | null) {
    if (regionId === (address.regionId ?? null)) return;
    try {
      await dispatch(
        updateAddress({
          businessId,
          addressId: address.addressId,
          regionId,
        })
      ).unwrap();
      toast({ severity: 'success', message: t('common.saved') });
    } catch (err) {
      toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
    }
  }

  async function handleRemove(address: Address) {
    try {
      await dispatch(
        removeAddress({ businessId, addressId: address.addressId })
      ).unwrap();
      toast({ severity: 'success', message: t('common.saved') });
    } catch (err) {
      toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
    }
  }

  async function handleAdd(fields: {
    name?: string;
    description?: string;
    street?: string;
    city?: string;
    zip?: string;
    countryId?: number | null;
    regionId?: number | null;
    primary?: boolean;
  }) {
    try {
      await dispatch(
        addAddress({
          businessId,
          ...fields,
          primary: fields.primary ?? addresses.length === 0,
        })
      ).unwrap();
      toast({ severity: 'success', message: t('common.saved') });
      setIsAdding(false);
    } catch (err) {
      toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
    }
  }

  // Label del summary: preferisce `name`, altrimenti street+city, fallback i18n.
  function addressSummary(address: Address): string {
    if (address.name?.trim()) return address.name.trim();
    const parts = [address.street, address.city].map((s) => addressText(s).trim()).filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : t('business.address');
  }

  return (
    <div className="list-editor">
      <div className="list-editor__label">{t('business.address')}</div>
      {addresses.map((address) => (
        <AddressCard
          key={address.addressId}
          address={address}
          summary={addressSummary(address)}
          commitField={commitAddressField}
          onTogglePrimary={handleTogglePrimary}
          onCountryChange={handleCountryChange}
          onRegionChange={handleRegionChange}
          onRemove={(address) => confirm.requestConfirm(
            t('editableField.removeAddressConfirm', { value: addressSummary(address) }),
            () => handleRemove(address),
          )}
        />
      ))}
      {isAdding ? (
        <NewAddressForm onConfirm={handleAdd} onCancel={() => setIsAdding(false)} />
      ) : (
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setIsAdding(true)}
          className="btn-lowercase mt-xs"
        >
          {t('common.add')}
        </Button>
      )}
      <ConfirmDialog
        open={confirm.isOpen}
        message={confirm.message}
        {...confirm.options}
        onConfirm={confirm.confirm}
        onCancel={confirm.cancel}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// AddressCard — singolo address come accordion
// ---------------------------------------------------------------------------

interface AddressCardProps {
  address: Address;
  summary: string;
  commitField: (
    address: Address,
    field: AddressStringField
  ) => (value: EditableValue) => Promise<void>;
  onTogglePrimary: (address: Address) => Promise<void> | void;
  onCountryChange: (address: Address, countryId: number | null) => Promise<void> | void;
  onRegionChange: (address: Address, regionId: number | null) => Promise<void> | void;
  onRemove: (address: Address) => Promise<void> | void;
}

function AddressCard({ address, summary, commitField, onTogglePrimary, onCountryChange, onRegionChange, onRemove }: AddressCardProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  return (
    <Accordion
      className="address-accordion"
      disableGutters
      elevation={0}
      expanded={expanded}
      onChange={(_, isExpanded) => setExpanded(isExpanded)}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box className="relation-header">
          {/* Stella "primario" nel summary, come nelle righe di
              EmailListEditor/PhoneListEditor: resta visibile anche ad accordion
              CHIUSO, cosi' si riconosce l'indirizzo principale senza aprirlo.
              `component="span"`: il summary e' gia' un <button>, e un button
              annidato sarebbe markup non valido (stesso motivo del cestino). */}
          <IconButton
            component="span"
            className="le-star"
            size="small"
            onClick={(e) => { e.stopPropagation(); void onTogglePrimary(address); }}
            disabled={!!address.primary}
            aria-label={address.primary ? t('editableField.primary') : t('editableField.setAsPrimary')}
            title={address.primary ? t('editableField.primary') : t('editableField.setAsPrimary')}
          >
            {address.primary ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
          </IconButton>
          {expanded ? <span /> : <Typography className="relation-title">{summary}</Typography>}
          <IconButton
            component="span"
            size="small"
            onClick={(e) => { e.stopPropagation(); void onRemove(address); }}
            aria-label={t('common.remove')}
            className="address-delete-btn"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      </AccordionSummary>
      <AccordionDetails className="address-accordion__details">
        <FieldRow label={t('address.name')}>
          <EditableField
            value={address.name ?? ''}
            variant={{ kind: 'text' }}
            onCommit={commitField(address, 'name')}
            label={t('address.name')}
          />
        </FieldRow>
        <FieldRow label={t('address.description')}>
          <EditableField
            value={address.description ?? ''}
            variant={{ kind: 'text' }}
            onCommit={commitField(address, 'description')}
            label={t('address.description')}
          />
        </FieldRow>
        <FieldRow label={t('address.street')}>
          <EditableField
            value={addressText(address.street)}
            variant={{ kind: 'text' }}
            onCommit={commitField(address, 'street')}
            label={t('address.street')}
          />
        </FieldRow>
        <FieldRow label={t('address.city')}>
          <EditableField
            value={addressText(address.city)}
            variant={{ kind: 'text' }}
            onCommit={commitField(address, 'city')}
            label={t('address.city')}
          />
        </FieldRow>
        <FieldRow label={t('address.zip')}>
          <EditableField
            value={addressText(address.zip)}
            variant={{ kind: 'text' }}
            onCommit={commitField(address, 'zip')}
            label={t('address.zip')}
          />
        </FieldRow>
        {/* country/region: editabili via lookup geography. Pattern
            inlineEditable (view flat + edit on double-click) per coerenza
            con gli EditableField sopra. Commit immediato al select →
            cambio country resetta automaticamente la region (vedi
            handleCountryChange nel parent). */}
        <FieldRow label={t('address.country')}>
          <CountrySelect
            value={address.countryId}
            onChange={(countryId) => void onCountryChange(address, countryId)}
            inlineEditable
            fullWidth
          />
        </FieldRow>
        <FieldRow label={t('address.region')}>
          <RegionSelect
            value={address.regionId}
            onChange={(regionId) => void onRegionChange(address, regionId)}
            countryId={address.countryId}
            inlineEditable
            fullWidth
          />
        </FieldRow>
      </AccordionDetails>
    </Accordion>
  );
}

function FieldRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="info-row">
      <div className="info-row__label">{label}</div>
      <div className="info-row__value">{children}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// NewAddressForm — mini-form inline per creare un nuovo address
// ---------------------------------------------------------------------------

interface NewAddressFormProps {
  onConfirm: (fields: {
    name?: string;
    description?: string;
    street?: string;
    city?: string;
    zip?: string;
    countryId?: number | null;
    regionId?: number | null;
    primary?: boolean;
  }) => void | Promise<void>;
  onCancel: () => void;
}

function NewAddressForm({ onConfirm, onCancel }: NewAddressFormProps) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [street, setStreet] = useState('');
  const [city, setCity] = useState('');
  const [zip, setZip] = useState('');
  const [countryId, setCountryId] = useState<number | null>(null);
  const [regionId, setRegionId] = useState<number | null>(null);

  function handleConfirm() {
    // Nessun campo strettamente required lato backend. Lato UI chiediamo
    // almeno street o name per avere un identificativo riconoscibile.
    if (!street.trim() && !name.trim()) return;
    void onConfirm({
      name: name.trim() || undefined,
      street: street.trim() || undefined,
      city: city.trim() || undefined,
      zip: zip.trim() || undefined,
      countryId: countryId ?? undefined,
      regionId: regionId ?? undefined,
    });
  }

  const canConfirm = !!street.trim() || !!name.trim();

  return (
    <div className="address-box address-box--new" style={{ marginTop: 8 }}>
      <TextField autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder={t('address.name')} size="small" fullWidth className="mb-xs" />
      <TextField value={street} onChange={(e) => setStreet(e.target.value)} placeholder={t('address.street')} size="small" fullWidth className="mb-xs" />
      <TextField value={city} onChange={(e) => setCity(e.target.value)} placeholder={t('address.city')} size="small" fullWidth className="mb-xs" />
      <TextField value={zip} onChange={(e) => setZip(e.target.value)} placeholder={t('address.zip')} size="small" fullWidth className="mb-xs" />
      {/* Reset region quando cambia country (semantica chiara nel parent). */}
      <CountrySelect
        value={countryId}
        onChange={(next) => { setCountryId(next); setRegionId(null); }}
        fullWidth
        className="mb-xs"
      />
      <RegionSelect
        value={regionId}
        onChange={setRegionId}
        countryId={countryId}
        fullWidth
        className="mb-xs"
      />
      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', marginTop: 4 }}>
        <Button size="small" onClick={onCancel} className="btn-lowercase">{t('common.cancel')}</Button>
        <Button size="small" variant="contained" onClick={handleConfirm} disabled={!canConfirm} className="btn-lowercase">
          {t('common.create')}
        </Button>
      </div>
    </div>
  );
}
